import React, { type ReactElement, useEffect, useState } from 'react';
import {
  faPersonCircleXmark,
  faPersonWalkingArrowLoopLeft
} from '@fortawesome/free-solid-svg-icons';
import TableActions from 'atom-components/table-actions-lite';
import { pluralise } from 'helpers/string.helpers';
import ConfirmDeallocateMarkerModal from 'pages/marking/components/confirm-deallocate-marker-modal';
import DeallocationResultsModal from 'pages/marking/components/deallocation-results-modal';
import { isAnActiveMarkerOfResponse } from 'pages/marking/helpers/marker.helpers';
import {
  markersOfResponseParams,
  marksOfResponseParams
} from 'pages/marking/helpers/request-params.helpers';
import type { ChangeMarkerMark } from 'pages/marking/types';
import MarkingAllocationPolicy from 'policies/marking-allocation.policy';
import { useCustomerUsers } from 'services/assist-api/entities/customer-user/customer-users';
import { useTestInstanceResponseMarks } from 'services/assist-api/entities/test-instance-response-mark/hooks';
import { useTestInstanceResponseMarkers } from 'services/assist-api/entities/test-instance-response-marker/hooks';
import type { TestInstanceResponseMarkerWithUser } from 'services/assist-api/entities/test-instance-response-marker/types';
import { useAuthCustomer } from 'services/providers/auth.hooks';
import { usePageAccess } from 'services/providers/page-access.provider';
import { isInitialLoad } from 'utils/check-query-status';
import ConfirmReloadTableModal from './confirm-reload-table-modal';
import DeallocateMarkersModal from './deallocate-markers-modal';
import ReallocateResponseModal from './reallocate-response-modal';

type Props = {
  responseId: number;
  markersCount: number;
  onRequestRefetch: () => void;
};

export default function AllocationTableActions({
  responseId,
  markersCount,
  onRequestRefetch
}: Props): ReactElement {
  enum TableAction {
    None = 'None',
    DeallocateMarker = 'deallocateMarker',
    ReallocateResponse = 'reallocateResponse'
  }
  enum Modal {
    None = 'none',
    ConfirmReloadTable = 'confirmReloadTable',
    DeallocationResults = 'DeallocationResults',
    ConfirmDeallocateMarker = 'confirmDeallocateMarker',
    ReallocateResponse = 'reallocateResponse',
    DeallocateMarkers = 'deallocateMarkers'
  }
  const [currentTableAction, setCurrentTableAction] = useState<TableAction>(TableAction.None);
  const [currentModal, setCurrentModal] = useState<Modal>(Modal.None);

  const { authorisableUser } = usePageAccess();

  const testInstanceResponseMarkersQuery =
    useTestInstanceResponseMarkers<TestInstanceResponseMarkerWithUser>(
      markersOfResponseParams(responseId),
      // Only run this query when a table action button is clicked for performance reasons:
      { enabled: currentTableAction !== TableAction.None }
    );
  const firstMarkerFetched = testInstanceResponseMarkersQuery.data?.json[0];

  // Fetch all organisation users who are markers of the response:
  const currentOrganisation = useAuthCustomer();
  const organisationUserIds = testInstanceResponseMarkersQuery.data?.json.map(
    (marker) => marker.user.id
  );
  const organisationUsersQuery = useCustomerUsers(
    {
      filters: {
        id: organisationUserIds,
        customer: currentOrganisation?.id
      }
    },
    { enabled: !!currentOrganisation?.id && testInstanceResponseMarkersQuery.isSuccess }
  );
  const firstOrganisationUserFetched = organisationUsersQuery.data?.json[0];

  // Fetch marks and mark items linked to a particular marker of the response. This is only called
  // when there is a single marker of the response in order to confirm if the Deallocation Results
  // modal should appear instead of the Confirm Deallocate Marker modal:
  const marksQuery = useTestInstanceResponseMarks<ChangeMarkerMark>(
    marksOfResponseParams(responseId),
    {
      enabled:
        testInstanceResponseMarkersQuery.isSuccess &&
        testInstanceResponseMarkersQuery.data?.json.length === 1
    }
  );

  const isLoading = isInitialLoad(testInstanceResponseMarkersQuery, organisationUsersQuery);

  useEffect(() => {
    if (
      currentTableAction !== TableAction.None &&
      testInstanceResponseMarkersQuery.isSuccess &&
      !isInitialLoad(marksQuery)
    ) {
      // Check the number of markers currently allocated to determine which of the three modals
      // should be displayed:
      switch (testInstanceResponseMarkersQuery.data?.json.length) {
        case 0:
          // Not normally displayed unless de/reallocation has been occurred in another tab and the
          // Marking Allocation table is out of date, resulting in the Deallocate Marker button not
          // being disabled when it should be:
          setCurrentModal(Modal.ConfirmReloadTable);
          break;
        case 1:
          // Ensure that the marks have been fetched before determining whether to show the Confirm
          // Deallocate Marker/Reallocate Response or the Deallocation Results modal instead for a
          // single marker. The frontend is responsible for blocking de/reallocation if the marker
          // has saved marking work.
          if (firstMarkerFetched && marksQuery.isSuccess) {
            if (isAnActiveMarkerOfResponse(firstMarkerFetched?.id, marksQuery.data?.json)) {
              // Prevents the marking admin from de/reallocating a single marker:
              setCurrentModal(Modal.DeallocationResults);
            } else {
              // Allows the marking admin to confirm the de/reallocation of a single marker:
              if (currentTableAction === TableAction.DeallocateMarker) {
                setCurrentModal(Modal.ConfirmDeallocateMarker);
              }
              if (currentTableAction === TableAction.ReallocateResponse) {
                setCurrentModal(Modal.ReallocateResponse);
              }
            }
          }
          break;
        default:
          // Allows the marking admin to de/reallocate multiple markers:
          setCurrentModal(Modal.DeallocateMarkers);
      }
      setCurrentTableAction(TableAction.None);
    }
  }, [
    testInstanceResponseMarkersQuery,
    marksQuery,
    firstMarkerFetched,
    currentTableAction,
    TableAction,
    Modal
  ]);

  /**
   * This count may not be up to date if some de/reallocations have been performed after the table
   * was loaded, but is sufficient for disabling the button. A further check will refetch the
   * markers when the button is clicked and show a message if there are no longer any markers.
   */
  const areTableActionsDisabled = markersCount === 0;

  return (
    <>
      <ConfirmReloadTableModal
        isOpen={currentModal === Modal.ConfirmReloadTable}
        onClose={(): void => setCurrentModal(Modal.None)}
        onConfirm={(): void => {
          setCurrentModal(Modal.None);
          onRequestRefetch();
        }}
        responseId={responseId}
      />
      {firstMarkerFetched && currentModal === Modal.DeallocationResults && (
        <DeallocationResultsModal
          isOpen={currentModal === Modal.DeallocationResults}
          onClose={(): void => setCurrentModal(Modal.None)}
          responseId={responseId}
          allocatedMarkerId={firstMarkerFetched.id}
          allocatedOrganisationUser={firstOrganisationUserFetched}
          onForceReallocate={(): void => onRequestRefetch()}
        />
      )}
      {firstMarkerFetched && currentModal === Modal.ConfirmDeallocateMarker && (
        <ConfirmDeallocateMarkerModal
          isOpen={currentModal === Modal.ConfirmDeallocateMarker}
          onClose={(): void => setCurrentModal(Modal.None)}
          onDeallocate={(): void => onRequestRefetch()}
          responseId={responseId}
          allocatedMarkerId={firstMarkerFetched.id}
          allocatedOrganisationUser={firstOrganisationUserFetched}
        />
      )}
      {firstMarkerFetched && currentModal === Modal.ReallocateResponse && (
        <ReallocateResponseModal
          isOpen={currentModal === Modal.ReallocateResponse}
          onClose={(): void => setCurrentModal(Modal.None)}
          onReallocate={(): void => onRequestRefetch()}
          responseId={responseId}
          allocatedMarkerId={firstMarkerFetched.id}
          allocatedOrganisationUser={firstOrganisationUserFetched}
          isLoading={isLoading}
        />
      )}
      {currentModal === Modal.DeallocateMarkers && (
        <DeallocateMarkersModal
          isOpen={currentModal === Modal.DeallocateMarkers}
          onClose={(): void => {
            setCurrentModal(Modal.None);
            onRequestRefetch();
          }}
          onDeallocate={(): void => {
            // After a single deallocation, we only want to reload the markers and their related
            // organisation users, rather than the whole table. This prevents the modal from closing
            // after a single deallocation as the marking admin may want to perform further
            // deallocations from the response.
            testInstanceResponseMarkersQuery.refetch();
          }}
          responseId={responseId}
          markersOfResponse={testInstanceResponseMarkersQuery.data?.json || []}
          organisationUsers={organisationUsersQuery.data?.json || []}
          isLoading={isLoading}
        />
      )}
      {MarkingAllocationPolicy.allocate(authorisableUser) && (
        <>
          <TableActions.Button
            tooltip={
              areTableActionsDisabled
                ? 'Cannot Deallocate (no markers allocated)'
                : `Deallocate ${pluralise('Marker', markersCount)}`
            }
            onClick={async (): Promise<void> => {
              // Ensure that we refetch the markers so that we have the most up to date marker count:
              await testInstanceResponseMarkersQuery.refetch();
              setCurrentTableAction(TableAction.DeallocateMarker);
            }}
            icon={faPersonCircleXmark}
            disabled={areTableActionsDisabled}
          />
          <TableActions.Button
            tooltip={
              areTableActionsDisabled
                ? 'Cannot Reallocate (no markers allocated)'
                : 'Reallocate Response'
            }
            onClick={async (): Promise<void> => {
              await testInstanceResponseMarkersQuery.refetch();
              setCurrentTableAction(TableAction.ReallocateResponse);
            }}
            icon={faPersonWalkingArrowLoopLeft}
            disabled={areTableActionsDisabled}
          />
        </>
      )}
    </>
  );
}
